import classNames from "classnames";
import range from "lodash-es/range";
import * as moment from "moment";
import VerbConstants from "motw_core/Constants/VerbConstants";
import { EDA } from "motw_core/Dreams/EDreamAttribute";
import IDream, { resourceNameDream } from "motw_core/Entity/IDream";
import {
    GetEntryPrefix,
    SaveLogEntryAsync,
} from "motw_core/Repositories/LogEntryRepository";
import * as React from "react";
import { HandleError } from "source/app/presentation/Shared/HandleError";
import { VerbIcon } from "source/app/presentation/Shared/RPGIcon/VerbIcons.tsx/VerbIcons";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import {
    OnErrorAddLogEntry,
    OnFinishAddLogEntry,
} from "source/app/services/Log/AddEntry";
import { Sounds } from "source/app/services/Sound/Sounds";
import { State } from "source/app/services/UI/State/State";
import UI from "source/app/services/UI/UI";
import * as toastr from "toastr";
import Data from "../../../../services/Data";
import { AreYouSure } from "../../../../services/Shared/RenderSwal/RenderSwal";
import { ESharedComponent } from "../../../../services/UI/State/ESharedComponent";
import { WithAnimatedProgressBarAsync } from "../../../Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import Render from "../../../Shared/Render";
import Cell from "../Cell/Cell";
import { randomDreamTarget } from "../DreamRow/DreamRow";
import { IDreamsState } from "../IDreamsState";
import { GetRandomDreamData } from "./GetRandomDream";

export const ExtendDream = (dream: IDream) => new DreamExtended(dream);

class DreamExtended {
    constructor(readonly dream: IDream) {}

    HasAttribute(attribute: EDA) {
        const attributeName = EDA[attribute];
        return this.dream.attributes.includes(attributeName);
    }
}

const DeleteDream = async (
    dream: IDream,
    allLinks: IDream[],
    showConfirmation: boolean = false,
    addCompletionLogEntry: boolean = false
) => {
    const rowId = dream.id;
    if (!rowId || !allLinks) throw new Error("Missing inputs");

    // Check if there are links pointing to this dream
    const hasLinksToIt =
        allLinks.filter((elem) => elem.linkid === rowId).length > 0;

    if (hasLinksToIt) {
        toastr.error(
            "Cannot delete this dream unless it has nothing linked to it"
        );

        return;
    }

    const hasFixedAttribute = dream.attributes.includes(EDA[EDA.fixed]);

    if (hasFixedAttribute) {
        toastr.error(
            `Cannot delete this dream because it has the "fixed" attribute`
        );

        return;
    }

    const DeleteCall = async () => {
        await ClientDataProvider.DeleteRecordsAsync(
            `${resourceNameDream}?id=eq.${rowId}`
        );

        await Data.Load.Single.ByStateTarget<IDreamsState>("dreamsdata");

        if (addCompletionLogEntry) {
            await PostCompletedDreamLogEntryAsync(dream);
        }
    };

    const DeleteAction = async (willDelete: boolean) => {
        if (willDelete) {
            WithAnimatedProgressBarAsync(DeleteCall);
        }
    };

    if (showConfirmation) {
        const actionName = addCompletionLogEntry ? `complete` : `delete`;

        AreYouSure(
            `Are you sure you want to ${actionName} this dream?`,
            DeleteAction
        );
    } else {
        await DeleteAction(true);
    }
};

export const difficultyRange = range(1, 12);

const GetAllLinks = (dream: IDream, allDreams: IDream[]) => {
    const finalLinkId = dream.linkid ? dream.linkid : dream.id;
    const isMasterDream = (dream: IDream) => dream.id === finalLinkId;
    const linksToMasterDream = (dream: IDream) => dream.linkid === finalLinkId;

    const links = allDreams
        .filter((dream) => isMasterDream(dream) || linksToMasterDream(dream))
        .sort((a, b) => (b.linkid === undefined ? 1 : 0)) // show master (non-linked) dream at bottom of list
        .sort((a, b) => a.difficulty - b.difficulty);

    return links;
};

export default {
    DeleteDream,
    GetAllLinks,
};

export function DreamEmotionSymbol({
    symbol,
    doesHaveLinks,
}: {
    symbol: string;
    doesHaveLinks: boolean;
}) {
    const tooltipText = doesHaveLinks ? "view related dreams" : "";

    const hasLinksStyleClass = StyleClass(`dreamAttributeIcons_hasParent`, {
        border: "1px dashed rgb(153 153 153 / 65%)",
        borderRadius: "5px",
        padding: `2px`,
    });

    return (
        <Render id="emotionSymbol" when={symbol !== undefined && symbol !== ""}>
            <Cell>
                <span
                    data-tip={tooltipText}
                    id="goalVerbSymbol"
                    className={classNames({
                        [hasLinksStyleClass]: doesHaveLinks,
                    })}
                >
                    <VerbIcon verbSymbol={symbol} />
                </span>
            </Cell>
        </Render>
    );
}

export function ActivateRandomDream() {
    const dreams = State.ReadClass<IDreamsState>(ESharedComponent.Dreams);

    if (!dreams) {
        return;
    }

    const randomDreamData = GetRandomDreamData(dreams.state.filteredData);

    if (randomDreamData === undefined) {
        return;
    }

    const topOffset = -0.6 * (window.outerHeight / 2);

    State.ReadClass<IDreamsState>(ESharedComponent.Dreams).setState(
        {
            randomDream: randomDreamData[0],
            remainingRandomDreams: randomDreamData[1],
        },
        () => {
            UI.ScrollToDiv(`#${randomDreamTarget}`, topOffset);
        }
    );
}

async function PostCompletedDreamLogEntryAsync(dream: IDream) {
    try {
        const verb = "Achieving";
        const entrySymbol = VerbConstants.GetSymbol(verb);

        const logEntry = [
            GetEntryPrefix(0, entrySymbol) +
                " " +
                verb +
                " completed dream: " +
                dream.name,
            "10", // kips
            "9",
            moment().format("MMM D YYYY HH:mm"),
            "1", // Set energy value to default because its not being used
        ];

        await SaveLogEntryAsync(
            logEntry,
            ClientDataProvider,
            true,
            OnErrorAddLogEntry,
            OnFinishAddLogEntry
        );

        toastr.success(`Completed dream`, "", {
            timeOut: 500,
        });

        Sounds.shoutWhoo.Play();
    } catch (e) {
        HandleError(e);
    }
}
